import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { translations } from "./translations";

const STORAGE_KEY = "racksforall-language";
const DEFAULT_LANGUAGE = "pt";
const I18nContext = createContext(null);

const originalText = new WeakMap();
const originalAttr = new WeakMap();
let translating = false;

function normalizeText(value) {
  return value.replace(/\s+/g, " ").trim();
}

function preserveOuterWhitespace(source, translated) {
  const leading = source.match(/^\s*/)?.[0] || "";
  const trailing = source.match(/\s*$/)?.[0] || "";
  return `${leading}${translated}${trailing}`;
}

function translateDynamic(text, language) {
  const rules = {
    en: [
      [/^(\d+) produto\(s\) nesta página$/, "$1 product(s) on this page"],
      [/^(\d+) item\(s\)$/, "$1 item(s)"],
      [/^(\d+) variantes disponíveis$/, "$1 variants available"],
      [/^1 variante disponível$/, "1 variant available"],
      [/^\((\d+) avaliações\)$/, "($1 reviews)"],
      [/^Página (\d+) de (\d+)$/, "Page $1 of $2"],
      [/^Cupão aplicado: (.+)$/, "Coupon applied: $1"],
      [/^REF: (.+)$/, "REF: $1"],
    ],
    es: [
      [/^(\d+) produto\(s\) nesta página$/, "$1 producto(s) en esta página"],
      [/^(\d+) item\(s\)$/, "$1 artículo(s)"],
      [/^(\d+) variantes disponíveis$/, "$1 variantes disponibles"],
      [/^1 variante disponível$/, "1 variante disponible"],
      [/^\((\d+) avaliações\)$/, "($1 reseñas)"],
      [/^Página (\d+) de (\d+)$/, "Página $1 de $2"],
      [/^Cupão aplicado: (.+)$/, "Cupón aplicado: $1"],
      [/^REF: (.+)$/, "REF: $1"],
    ],
    de: [
      [/^(\d+) produto\(s\) nesta página$/, "$1 Produkt(e) auf dieser Seite"],
      [/^(\d+) item\(s\)$/, "$1 Artikel"],
      [/^(\d+) variantes disponíveis$/, "$1 Varianten verfügbar"],
      [/^1 variante disponível$/, "1 Variante verfügbar"],
      [/^\((\d+) avaliações\)$/, "($1 Bewertungen)"],
      [/^Página (\d+) de (\d+)$/, "Seite $1 von $2"],
      [/^Cupão aplicado: (.+)$/, "Gutschein angewendet: $1"],
      [/^REF: (.+)$/, "REF: $1"],
    ],
    it: [
      [/^(\d+) produto\(s\) nesta página$/, "$1 prodotto/i in questa pagina"],
      [/^(\d+) item\(s\)$/, "$1 articolo/i"],
      [/^(\d+) variantes disponíveis$/, "$1 varianti disponibili"],
      [/^1 variante disponível$/, "1 variante disponibile"],
      [/^\((\d+) avaliações\)$/, "($1 recensioni)"],
      [/^Página (\d+) de (\d+)$/, "Pagina $1 di $2"],
      [/^Cupão aplicado: (.+)$/, "Coupon applicato: $1"],
      [/^REF: (.+)$/, "REF: $1"],
    ],
    fr: [
      [/^(\d+) produto\(s\) nesta página$/, "$1 produit(s) sur cette page"],
      [/^(\d+) item\(s\)$/, "$1 article(s)"],
      [/^(\d+) variantes disponíveis$/, "$1 variantes disponibles"],
      [/^1 variante disponível$/, "1 variante disponible"],
      [/^\((\d+) avaliações\)$/, "($1 avis)"],
      [/^Página (\d+) de (\d+)$/, "Page $1 sur $2"],
      [/^Cupão aplicado: (.+)$/, "Coupon appliqué: $1"],
      [/^REF: (.+)$/, "REF: $1"],
    ],
  };

  for (const [pattern, replacement] of rules[language] || []) {
    if (pattern.test(text)) return text.replace(pattern, replacement);
  }

  return null;
}

function translateValue(value, language) {
  if (language === DEFAULT_LANGUAGE) return value;

  const normalized = normalizeText(value);
  if (!normalized) return value;

  const dictionary = translations[language] || {};
  const translated = dictionary[normalized] || translateDynamic(normalized, language);
  return translated ? preserveOuterWhitespace(value, translated) : value;
}

function walkNodes(root, callback) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes = [];
  let current = walker.nextNode();

  while (current) {
    nodes.push(current);
    current = walker.nextNode();
  }

  nodes.forEach(callback);
}

function translateAttributes(element, language) {
  const attributes = ["placeholder", "aria-label", "alt", "title", "content"];

  attributes.forEach((attr) => {
    if (!element.hasAttribute?.(attr)) return;

    let stored = originalAttr.get(element);
    if (!stored) {
      stored = {};
      originalAttr.set(element, stored);
    }

    if (!stored[attr]) stored[attr] = element.getAttribute(attr);

    const source = stored[attr];
    const next = language === DEFAULT_LANGUAGE ? source : translateValue(source, language);

    if (element.getAttribute(attr) !== next) {
      element.setAttribute(attr, next);
    }
  });
}

function translateDocument(language) {
  if (typeof document === "undefined" || translating) return;

  translating = true;

  try {
    document.documentElement.lang = language;

    walkNodes(document, (node) => {
      const source = originalText.get(node) || node.nodeValue;
      if (!originalText.has(node)) originalText.set(node, source);

      const next = language === DEFAULT_LANGUAGE ? source : translateValue(source, language);
      if (node.nodeValue !== next) node.nodeValue = next;
    });

    document.querySelectorAll("*").forEach((element) => translateAttributes(element, language));
  } finally {
    translating = false;
  }
}

function getInitialLanguage() {
  if (typeof window === "undefined") return DEFAULT_LANGUAGE;

  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (translations[saved]) return saved;

  const browserLanguage = window.navigator.language?.slice(0, 2);
  return translations[browserLanguage] ? browserLanguage : DEFAULT_LANGUAGE;
}

export function I18nProvider({ children }) {
  const [language, setLanguageState] = useState(getInitialLanguage);

  const setLanguage = useCallback((nextLanguage) => {
    if (!translations[nextLanguage]) return;

    window.localStorage.setItem(STORAGE_KEY, nextLanguage);
    setLanguageState(nextLanguage);
  }, []);

  const t = useCallback(
    (value) => (language === DEFAULT_LANGUAGE ? value : translateValue(value, language)),
    [language]
  );

  useEffect(() => {
    translateDocument(language);

    const observer = new MutationObserver(() => {
      window.requestAnimationFrame(() => translateDocument(language));
    });

    observer.observe(document, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ["placeholder", "aria-label", "alt", "title", "content"],
    });

    return () => observer.disconnect();
  }, [language]);

  const value = useMemo(
    () => ({ language, setLanguage, t }),
    [language, setLanguage, t]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) throw new Error("useI18n must be used inside I18nProvider");
  return context;
}
